import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { StringFormatter } from '@repo/toolkit/string'
import Box from '@repo/ui/box'
import { BarGraph, PieChart } from '@repo/ui/graphs'
import { ChartIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'

import { getPcr } from '../../api/pcr/get-pcr'
import { getPcrStaffApplicationsStatistics } from '../../api/staff-application/get-applications-statistics'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import useAuth from '../../lib/hooks/use-auth'
import {
  StaffApplicationStatusEnum,
  staffApplicationStatusEnumMapping
} from '../../lib/types/enums/staff-application/staff-application-status.enum'
import { RoleEnum } from '../../lib/types/enums/users/role.enum'
import NotFoundPage from '../common/not-found'

export const PcrStaffApplicationStatisticsPage = () => {
  const { user } = useAuth()
  const { id } = useParams<{ id: string }>()
  // a PCR always sees their own statistics, an admin the ones of the PCR in the URL
  const pcrId = user?.pcr?.id ?? Number(id)
  const { data, isLoading } = useQuery({
    queryKey: ['pcr-staff-applications-statistics', pcrId],
    queryFn: () => getPcrStaffApplicationsStatistics(pcrId),
    enabled: Number.isInteger(pcrId)
  })
  // the admin needs the PCR's name in the title to tell whose statistics are shown
  const { data: pcr } = useQuery({
    queryKey: ['pcr', pcrId],
    queryFn: () => getPcr(pcrId),
    enabled: user?.role === RoleEnum.ADMIN && Number.isInteger(pcrId)
  })

  const statusesCount = useMemo(() => {
    return data?.data.reduce(
      (acc, item) => {
        acc[item.status] = item.count
        return acc
      },
      {} as Record<StaffApplicationStatusEnum, number>
    )
  }, [data])

  const barGraphData = useMemo(
    () =>
      data?.data.map((item) => ({
        key: staffApplicationStatusEnumMapping[item.status].label,
        value: item.count
      })) ?? [],
    [data]
  )

  const PieChartData = statusesCount
    ? [
        {
          key: 'Acceptées',
          value: statusesCount[StaffApplicationStatusEnum.ACCEPTED],
          fill: tailwindColors.green.DEFAULT
        },
        {
          key: 'Rejetées',
          value:
            (statusesCount[StaffApplicationStatusEnum.REJECTED_AFTER_INTERVIEW] ?? 0) +
            (statusesCount[StaffApplicationStatusEnum.REJECTED_BEFORE_INTERVIEW] ?? 0),
          fill: tailwindColors.red.DEFAULT
        }
      ]
    : []
  if (isLoading || !user) return <LoadingScreen />
  if (!data) return <NotFoundPage />
  return (
    <div>
      <UserNavBar
        title={
          user.role === RoleEnum.ADMIN && pcr
            ? `${StringFormatter.capitalize(pcr.firstName)} ${pcr.lastName.toUpperCase()} - Statistiques`
            : 'Statistiques de base'
        }
        icon={ChartIcon}
      />
      <div className={'h-4'} />
      <div className={'flex gap-6 flex-col'}>
        {/* Bar graph showing the number of applications in each application status */}
        <Box className={'flex gap-7 flex-col py-7'}>
          <Typography variant={'sub-heading'}>Nombre de candidatures par statut (%)</Typography>
          <BarGraph
            title={'Répartition des candidatures par statut'}
            description={
              data.startDate && data.endDate
                ? DateFormatter.getDateRangeDDMMYYYY(data.startDate, data.endDate)
                : ''
            }
            data={barGraphData}
            yAxisLabel={'Nombre de candidatures'}
            xAxisLabel={'Statuts'}
          />
        </Box>
        {/* Pie chart showing the number and percentage of rejected and accepted applications */}
        <Box className={'flex gap-7 flex-col py-7 min-w-[400px] min-h-[450px]'}>
          <Typography variant={'sub-heading'}>
            Nombre de candidatures acceptées / rejetées
          </Typography>
          <PieChart data={PieChartData} />
        </Box>
      </div>
    </div>
  )
}
